// =============================================================================
// Planning Sidebar - Navigation pour le module Planification
// Onglets: Timeline, Charge semaine, Gantt équipe
// =============================================================================

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  GanttChart,
  LayoutGrid,
  Thermometer,
  Calendar,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface PlanningSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const NAV_ITEMS = [
  {
    path: '/planning',
    icon: Calendar,
    label: 'Calendrier',
    description: 'Vue calendrier cross-projets',
  },
  {
    path: '/planning/timeline',
    icon: CalendarDays,
    label: 'Timeline',
    description: 'Vue chronologique par membre',
  },
  {
    path: '/planning/workload',
    icon: LayoutGrid,
    label: 'Charge de travail',
    description: 'Charge par membre et par jour',
  },
  {
    path: '/planning/heatmap',
    icon: Thermometer,
    label: 'Disponibilité',
    description: 'Heatmap de charge des membres',
  },
  {
    path: '/planning/gantt',
    icon: GanttChart,
    label: 'Gantt équipe',
    description: 'Diagramme de Gantt par membre',
  },
];

const COLORS = {
  bg: '#FFFFFF',
  bgHover: '#F0FDFA',
  bgActive: '#CCFBF1',
  border: '#E2E8F0',
  textPrimary: '#0F172A',
  textSecondary: '#64748B',
  accent: '#0D9488', // Teal matching the module color
};

export function PlanningSidebar({ collapsed, onToggle }: PlanningSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) => {
    if (path === '/planning') {
      return location.pathname === '/planning';
    }
    return location.pathname === path;
  };

  const handleNavigation = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    navigate(path);
  };

  if (collapsed) {
    return (
      <aside
        className="flex flex-col w-12 shrink-0 border rounded-xl h-full"
        style={{ backgroundColor: COLORS.bg, borderColor: COLORS.border }}
      >
        <div
          className="h-14 flex items-center justify-center border-b"
          style={{ borderColor: COLORS.border }}
        >
          <Button
            variant="ghost"
            size="icon"
            className="w-8 h-8"
            style={{ color: COLORS.textSecondary }}
            onClick={onToggle}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <ScrollArea className="flex-1">
          <div className="flex flex-col items-center py-3 gap-1">
            {NAV_ITEMS.map((item) => {
              if (!item?.icon) return null;
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>
                    <a
                      href={item.path}
                      onClick={(e) => handleNavigation(e, item.path)}
                      className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                      style={{
                        backgroundColor: active ? COLORS.bgActive : 'transparent',
                      }}
                      onMouseEnter={(e) => {
                        if (!active) e.currentTarget.style.backgroundColor = COLORS.bgHover;
                      }}
                      onMouseLeave={(e) => {
                        if (!active) e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <Icon
                        className="h-4 w-4"
                        style={{
                          color: active ? COLORS.accent : COLORS.textSecondary,
                          strokeWidth: active ? 2.5 : 2,
                        }}
                      />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    style={{ backgroundColor: '#1E293B', color: '#FFFFFF', borderColor: '#334155' }}
                  >
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </ScrollArea>
      </aside>
    );
  }

  return (
    <aside
      className="flex flex-col w-56 shrink-0 border rounded-xl h-full"
      style={{ backgroundColor: COLORS.bg, borderColor: COLORS.border }}
    >
      <div
        className="h-14 flex items-center justify-between px-4 border-b"
        style={{ borderColor: COLORS.border }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <CalendarDays className="h-4 w-4 shrink-0" style={{ color: COLORS.accent }} />
          <span className="font-semibold text-sm truncate" style={{ color: COLORS.textPrimary }}>
            Planification
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="w-7 h-7 shrink-0"
          style={{ color: COLORS.textSecondary }}
          onClick={onToggle}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <nav className="p-2 space-y-1">
          {NAV_ITEMS.map((item) => {
            if (!item?.icon) return null;
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <a
                key={item.path}
                href={item.path}
                onClick={(e) => handleNavigation(e, item.path)}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer',
                  'hover:bg-[#F1F5F9]'
                )}
                style={{
                  backgroundColor: active ? COLORS.bgActive : 'transparent',
                  color: active ? COLORS.accent : COLORS.textPrimary,
                  fontWeight: active ? 500 : 400,
                }}
              >
                <Icon
                  className="h-4 w-4 shrink-0"
                  style={{
                    color: active ? COLORS.accent : COLORS.textSecondary,
                    strokeWidth: active ? 2.5 : 2,
                  }}
                />
                <span className="truncate">{item.label}</span>
              </a>
            );
          })}
        </nav>
      </ScrollArea>

      <div className="p-3 border-t" style={{ borderColor: COLORS.border }}>
        <p className="text-xs truncate" style={{ color: COLORS.textSecondary }}>
          Vue globale cross-projets
        </p>
      </div>
    </aside>
  );
}
